import React from 'react';
import { useLocation } from 'react-router-dom';
import ProductPage from '../components/seo/ProductPage';
import { isEnglishPath } from '../utils/i18nRoutes';

const ALTERNATES = [
  { hreflang: 'es', href: '/halo-i' },
  { hreflang: 'en', href: '/en/halo-i' },
  { hreflang: 'x-default', href: '/halo-i' },
];

const CONTENT = {
  es: {
    seoTitle: 'HALO-I | Casco inteligente EPI conectado | Valoriza',
    seoDescription: 'EPI conectado: casco IoT que monitoriza en tiempo real las condiciones de seguridad de quienes trabajan en calle. 13h de batería, SOS y geovalla.',
    breadcrumbLabel: 'HALO-I',
    tag: 'EPI inteligente',
    titleMain: 'HALO-I: el casco que',
    titleAccent: 'cuida',
    lead: 'EPI conectado: casco IoT que monitoriza en tiempo real las condiciones de seguridad de quienes trabajan en calle. Conectado a HaloTech para alertas centralizadas y geovalla.',
    problema: 'Los operarios que trabajan en entornos exteriores o de alto riesgo enfrentan peligros constantes. Garantizar su seguridad y tener la capacidad de actuar rápidamente en caso de emergencia es crucial. La falta de monitorización en tiempo real de su estado y ubicación puede retrasar la asistencia necesaria.',
    comoFunciona: 'HALO-I es un casco equipado con sensores IoT que monitorizan continuamente las condiciones de seguridad del operario y su entorno. Incluye funcionalidades como SOS, geofencing y monitorización de parámetros vitales. Los datos se transmiten a la plataforma HaloTech, permitiendo la alerta temprana y la gestión centralizada de incidencias.',
    cifras: [{ value: '13h', label: 'Batería' }, { value: 'SOS', label: 'Alerta directa' }, { value: 'Geovalla', label: 'Control de zona' }],
    paraQuien: 'Empresas de servicios urbanos, construcción, infraestructuras y cualquier sector con operarios en campo que requieran monitorización de seguridad avanzada. Responsables de prevención de riesgos laborales y equipos de operaciones.',
    stack: ['IoT', 'Wearables', 'HaloTech Cloud', 'Geoposicionamiento'],
    enlaces: [{ label: 'Canaria+: el golpe de calor se ve venir', to: '/canaria' }, { label: 'Exoesqueleto: menos carga, mismo turno', to: '/exoesqueleto' }, { label: 'Retos: reducción de riesgos físicos críticos', to: '/retos' }],
    ctaText: '¿Tienes un reto parecido?',
  },
  en: {
    seoTitle: 'HALO-I | Connected smart safety helmet | Valoriza',
    seoDescription: 'Connected PPE: an IoT helmet that monitors the safety conditions of street workers in real time. 13h battery life, SOS and geofencing.',
    breadcrumbLabel: 'HALO-I',
    tag: 'Smart PPE',
    titleMain: 'HALO-I: the helmet that',
    titleAccent: 'cares',
    lead: 'Connected PPE: an IoT helmet that monitors the safety conditions of street workers in real time. Connected to HaloTech for centralized alerts and geofencing.',
    problema: 'Workers operating outdoors or in high-risk environments face constant hazards. Guaranteeing their safety and being able to act quickly in an emergency is crucial. The lack of real-time monitoring of their status and location can delay the assistance they need.',
    comoFunciona: 'HALO-I is a helmet equipped with IoT sensors that continuously monitor the worker\u2019s safety conditions and surroundings. It includes features such as SOS, geofencing and vital-parameter monitoring. Data is transmitted to the HaloTech platform, enabling early alerts and centralized incident management.',
    cifras: [{ value: '13h', label: 'Battery life' }, { value: 'SOS', label: 'Direct alert' }, { value: 'Geofence', label: 'Zone control' }],
    paraQuien: 'Urban services, construction and infrastructure companies, and any sector with field workers that require advanced safety monitoring. Occupational risk-prevention officers and operations teams.',
    stack: ['IoT', 'Wearables', 'HaloTech Cloud', 'Geopositioning'],
    enlaces: [{ label: 'Canaria+: you can see heatstroke coming', to: '/en/canaria' }, { label: 'Exoskeleton: less load, same shift', to: '/en/exoesqueleto' }, { label: 'Challenges: reducing critical physical risks', to: '/en/challenges' }],
    ctaText: 'Do you have a similar challenge?',
  },
};

export default function HaloIPage() {
  const { pathname } = useLocation();
  const locale = isEnglishPath(pathname) ? 'en' : 'es';
  const c = CONTENT[locale];
  const path = locale === 'en' ? '/en/halo-i' : '/halo-i';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'HALO-I',
    description: c.seoDescription,
    url: `https://innovation.valorizasm.com${path}`,
    brand: { '@id': 'https://innovation.valorizasm.com/#organization' },
  };

  return (
    <ProductPage
      testId="halo-i-page"
      path={path}
      locale={locale}
      alternates={ALTERNATES}
      jsonLd={[jsonLd]}
      problemaLabel={locale === 'en' ? 'The problem' : 'El problema'}
      comoFuncionaLabel={locale === 'en' ? 'How it works' : 'Cómo funciona'}
      cifrasLabel={locale === 'en' ? 'Figures' : 'Cifras'}
      paraQuienLabel={locale === 'en' ? "Who it's for" : 'Para quién'}
      enlacesLabel={locale === 'en' ? 'Links' : 'Enlaces'}
      {...c}
    />
  );
}
